import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Zap, Cpu, ThermometerSun, Bell, ClipboardList, Leaf, AlertTriangle, MapPin, X, Sun, ExternalLink } from 'lucide-react';
import type { DetailCards } from '@/api/powerplantDetail';
import type { WeatherData } from '@/api/weather';
import { useI18n } from '@/i18n';
import { WeatherCard } from '@/ui/powerplants/weather-interaction';

const ALARM_TYPES = [
  { key: 'alarmWarningCount', label: 'Warning', color: 'bg-yellow-400' },
  { key: 'alarmMinorCount',   label: 'Minor',   color: 'bg-blue-400' },
  { key: 'alarmMajorCount',   label: 'Major',   color: 'bg-red-400' },
] as const;

const TASK_TYPES = [
  { key: 'taskTodoCount',       label: 'ToDo',     color: 'bg-gray-400' },
  { key: 'taskInProgressCount', label: 'Progress', color: 'bg-yellow-400' },
  { key: 'taskDoneCount',       label: 'Done',     color: 'bg-green-500' },
  { key: 'taskCanceledCount',   label: 'Cancel',   color: 'bg-red-500' },
] as const;

function LocationModal({ latitude, longitude, plantName, onClose }: {
  city?: string; town?: string; latitude: number; longitude: number; plantName: string; onClose: () => void;
}) {
  const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}&z=15&t=k&output=embed`;
  const mapsExternalUrl = `https://www.google.com/maps?q=${latitude},${longitude}&z=15&t=k`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-800 truncate">{plantName}</h3>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={mapsExternalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Google Maps'te Aç
            </a>
            <button className="rounded-full p-1 text-gray-500 hover:bg-gray-100" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="h-80 w-full overflow-hidden rounded">
          <iframe src={mapUrl} width="100%" height="100%" loading="lazy" allowFullScreen className="rounded" />
        </div>
      </div>
    </div>
  );
}

interface Props {
  cards: DetailCards;
  weather?: WeatherData | null;
  plantUuid?: string;
}

function DetailStatCards({ cards, weather, plantUuid }: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [locationOpen, setLocationOpen] = useState(false);

  const {
    dailyTotalPower, weeklyTotalPower, monthlyTotalPower, yearlyTotalPower, lifetimeTotalPower,
    invertersCount, activeInvertersCount,
    sensorsCount, activeSensorsCount,
    alarmTotalCount, taskTotalCount,
    plantCity, plantTown, plantLatitude, plantLongitude, plantName,
  } = cards;

  const invPct    = invertersCount > 0 ? (activeInvertersCount / invertersCount) * 100 : 0;
  const sensorPct = sensorsCount   > 0 ? (activeSensorsCount   / sensorsCount)   * 100 : 0;
  const alarmPcts = ALARM_TYPES.map(({ key }) =>
    alarmTotalCount > 0 ? ((cards[key] ?? 0) / alarmTotalCount) * 100 : 0,
  );

  const energyCardCls = 'flex min-w-0 flex-col justify-between gap-4 rounded-2xl border border-t-4 bg-gradient-to-br p-4 shadow-sm';
  const cardCls = 'flex min-w-0 flex-col justify-between gap-1 rounded-xl border border-t-4 bg-gradient-to-br to-white px-3 py-2 shadow-sm min-h-[85px]';
  const weatherCardCls = `${cardCls} border-amber-200 border-t-amber-500 from-amber-50`;

  return (
    <>
      {/* Enerji satırı */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: t('powerPlants.instantGeneration'), value: cards.instantPowerKw == null ? '—' : cards.instantPowerKw >= 1000 ? `${(cards.instantPowerKw / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} MW` : `${cards.instantPowerKw.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} kW`, color: 'text-violet-800', surface: 'border-violet-200 border-t-violet-500 from-violet-50 to-white', icon: 'bg-violet-100 text-violet-700' },
          { label: t('powerPlants.daily'),    value: dailyTotalPower,    color: 'text-amber-800', surface: 'border-amber-200 border-t-amber-500 from-amber-50 to-white', icon: 'bg-amber-100 text-amber-700' },
          { label: t('powerPlants.weekly'),   value: weeklyTotalPower,   color: 'text-sky-800', surface: 'border-sky-200 border-t-sky-500 from-sky-50 to-white', icon: 'bg-sky-100 text-sky-700' },
          { label: t('powerPlants.monthly'),  value: monthlyTotalPower,  color: 'text-emerald-800', surface: 'border-emerald-200 border-t-emerald-500 from-emerald-50 to-white', icon: 'bg-emerald-100 text-emerald-700' },
          { label: t('powerPlants.yearly'),   value: yearlyTotalPower,   color: 'text-orange-800', surface: 'border-orange-200 border-t-orange-500 from-orange-50 to-white', icon: 'bg-orange-100 text-orange-700' },
          { label: t('powerPlants.lifetime'), value: lifetimeTotalPower, color: 'text-blue-800', surface: 'border-blue-200 border-t-blue-600 from-blue-50 to-white', icon: 'bg-blue-100 text-blue-700' },
        ].map(({ label, value, color, surface, icon }) => (
          <div key={label} className={`${energyCardCls} ${surface}`}>
            <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${color}`}>
              <span className={`flex shrink-0 items-center justify-center rounded-lg p-2 ${icon}`}><Zap className="h-4 w-4" /></span>
              {label}
            </div>
            <div className="break-words text-2xl font-bold leading-tight tracking-tight text-slate-900 tabular-nums xl:text-3xl">{value}</div>
          </div>
        ))}
      </div>

      {/* Operasyonel satır */}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">

        {/* Inverter */}
        <div
          className={`${cardCls} border-emerald-200 border-t-emerald-500 from-emerald-50 ${plantUuid ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
          onClick={() => plantUuid && navigate(`/dashboard/powerplants/${plantUuid}/inverters`)}
        >
          <div className="flex items-center gap-2 text-gray-700">
            <span className="rounded-md bg-emerald-100 p-1 text-emerald-700"><Cpu className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-semibold">{t('powerPlants.inverter')}: {invertersCount}</span>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
            <span className="flex items-center gap-1 text-green-600"><Leaf className="h-3 w-3" /> {t('powerPlants.active')}: {activeInvertersCount}</span>
            <span className="flex items-center gap-1 text-red-500"><AlertTriangle className="h-3 w-3" /> {t('powerPlants.passive')}: {invertersCount - activeInvertersCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${invPct}%` }} /></div>
            <span className="text-[10px] font-semibold text-emerald-800 tabular-nums">{invPct.toFixed(1)}%</span>
          </div>
        </div>

        {/* Sensor */}
        <div className={`${cardCls} border-cyan-200 border-t-cyan-500 from-cyan-50`}>
          <div className="flex items-center gap-2 text-gray-700">
            <span className="rounded-md bg-cyan-100 p-1 text-cyan-700"><ThermometerSun className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-semibold">{t('powerPlants.sensor')}: {sensorsCount}</span>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
            <span className="flex items-center gap-1 text-green-600"><Leaf className="h-3 w-3" /> {t('powerPlants.active')}: {activeSensorsCount}</span>
            <span className="flex items-center gap-1 text-red-500"><AlertTriangle className="h-3 w-3" /> {t('powerPlants.passive')}: {sensorsCount - activeSensorsCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${sensorPct}%` }} /></div>
            <span className="text-[10px] font-semibold text-cyan-800 tabular-nums">{sensorPct.toFixed(1)}%</span>
          </div>
        </div>

        {/* Alarm */}
        <div className={`${cardCls} border-rose-200 border-t-rose-500 from-rose-50`}>
          <div className="flex items-center gap-2 text-gray-700">
            <span className="rounded-md bg-rose-100 p-1 text-rose-700"><Bell className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-semibold">{t('powerPlants.alarm')}: {alarmTotalCount}</span>
          </div>
          <div className="flex h-4 w-full overflow-hidden rounded-full bg-gray-100 text-[10px] font-semibold">
            {alarmPcts.map((pct, i) =>
              pct > 0 ? (
                <div
                  key={ALARM_TYPES[i].key}
                  className={`${ALARM_TYPES[i].color} flex items-center justify-center text-white`}
                  style={{ width: `${pct}%`, minWidth: 20 }}
                  title={`${ALARM_TYPES[i].label}: ${cards[ALARM_TYPES[i].key]}`}
                >
                  {cards[ALARM_TYPES[i].key]}
                </div>
              ) : null,
            )}
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-600">
            {ALARM_TYPES.map(({ key, label, color }) => (
              <span key={key} className="flex items-center gap-1">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${color}`} />
                {(label === 'Warning' ? t('home.alarms.warning') : label === 'Minor' ? t('home.alarms.minor') : t('home.alarms.major'))}: {cards[key]}
              </span>
            ))}
          </div>
        </div>

        {/* Task */}
        <div className={`${cardCls} border-blue-200 border-t-blue-500 from-blue-50`}>
          <div className="flex items-center gap-2 text-gray-700">
            <span className="rounded-md bg-blue-100 p-1 text-blue-700"><ClipboardList className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-semibold">{t('powerPlants.task')}: {taskTotalCount}</span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-[10px] font-semibold text-white">
            {TASK_TYPES.map(({ key, label, color }) => (
              <div key={key} className={`${color} flex items-center justify-center rounded px-1 py-0.5`}>
                {(label === 'ToDo' ? t('home.tasks.todo') : label === 'Progress' ? t('home.tasks.progress') : label === 'Done' ? t('home.tasks.done') : t('home.tasks.cancel'))}: {cards[key]}
              </div>
            ))}
          </div>
        </div>

        {/* Location */}
        <button
          className={`${cardCls} border-orange-200 border-t-orange-500 from-orange-50 cursor-pointer hover:shadow-md transition-shadow text-left`}
          onClick={() => setLocationOpen(true)}
        >
          <div className="flex items-center gap-2 text-gray-700">
            <span className="rounded-md bg-orange-100 p-1 text-orange-700"><MapPin className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-semibold">{t('powerPlants.location')}</span>
          </div>
          <div className="flex items-center justify-between gap-1">
            <div>
              <div className="text-sm font-bold text-blue-600">{plantCity}</div>
              <div className="text-xs font-medium text-green-600">{plantTown}</div>
            </div>
            <div className="h-7 w-12 flex-shrink-0 overflow-hidden rounded border border-gray-100 relative pointer-events-none">
              <div className="absolute inset-0 z-10" />
              <iframe
                src={`https://www.google.com/maps?q=${Number(plantLatitude)},${Number(plantLongitude)}&z=15&t=k&output=embed`}
                width="100%" height="100%" loading="lazy" className="h-full w-full" tabIndex={-1}
              />
            </div>
          </div>
          <div className="text-[10px] text-gray-500">{Number(plantLatitude).toFixed(4)}, {Number(plantLongitude).toFixed(4)}</div>
        </button>

        {/* Weather */}
        {weather ? (
          <WeatherCard
            weather={weather}
            plantName={plantName}
            className={weatherCardCls}
            compact
            title={t('common.weather')}
            fetchedAtLabel={t('powerPlants.weatherFetchedAt')}
          />
        ) : (
          <div className={weatherCardCls}>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="rounded-md bg-amber-100 p-1 text-amber-700"><Sun className="h-3.5 w-3.5" /></span>
              <span className="font-semibold">{t('common.weather')}</span>
            </div>
            <div className="text-sm text-gray-400">{t('powerPlants.weatherLoading')}</div>
          </div>
        )}
      </div>

      {locationOpen && createPortal(
        <LocationModal
          city={plantCity} town={plantTown}
          latitude={plantLatitude} longitude={plantLongitude}
          plantName={plantName}
          onClose={() => setLocationOpen(false)}
        />,
        document.body,
      )}
    </>
  );
}

export default memo(DetailStatCards);
